import { PropsWithChildren } from 'react';
import { IBasePreferenceItem } from '@/creditflowresearch/application/hooks/userPreferences/helpers';
import LockedFeaturePopup from './LockedFeaturePopup';

import styles from './lockedFeaturePopupContent.module.scss';

interface IProps extends PropsWithChildren {
  isLocked: boolean,
  /** What the control stands for — several items only when one control covers them all. */
  items: IBasePreferenceItem[],
}

/** "Asset-Backed (ABS)" — one copy formula for every locked product/region/market control. */
const toCopy = (items: IBasePreferenceItem[]) => {
  const abbreviations: string[] = [];
  const labels: string[] = [];

  items.forEach((item) => {
    const entity = item.getEntity();

    if (!entity) return;

    abbreviations.push(entity.getAbbreviation());
    labels.push(`${ entity.getDisplayName() } (${ entity.getAbbreviation() })`);
  });

  return { name: abbreviations.join(', '), content: labels.join(', ') };
};

/**
 * A locked control stays disabled but must still explain itself, so every disabled-by-plan
 * preference control opens the same upgrade popup on click. That works because the uikit
 * Radio/Checkbox/ToggleButtonGroup.Item keep their visible element clickable — only the
 * hidden input (or a `pointer-events: none` button) is disabled — so the click reaches the
 * popover trigger while the control keeps its disabled semantics.
 */
const LockedPreferenceControl = ({ isLocked, items, children }: IProps) => {
  if (!isLocked) {
    return <>{ children }</>;
  }

  const { name, content } = toCopy(items);

  return (
    <LockedFeaturePopup
      name={ name }
      content={ content }
      popoverContentClassName={ styles.popoverContainer }
    >
      { children }
    </LockedFeaturePopup>
  );
};

export default LockedPreferenceControl;
